/**
 * Generic confirmation/form modal built on the native <dialog> element.
 *
 * Returns a Promise<values | null>:
 *   • resolves to a {fieldName: value} map on confirm
 *   • resolves to null on cancel/backdrop click/Esc
 *
 * Usage:
 *   const v = await openModal({
 *     title: 'Завершить задачу',
 *     fields: [{ name: 'actual_minutes_spent', label: 'Минут потрачено',
 *                type: 'number', placeholder: 'опционально' }],
 *     confirmLabel: 'Завершить',
 *   });
 *   if (v) await api.completeTask(id, v);
 *
 * Рендер поля, сбор тела и привязка ref вынесены в components/record-field.js
 * (УИ-2a) — общий код с правой панелью записи (components/record-panel.js).
 */
import { renderField, bindRefField, collectFields, escapeHtml } from './record-field.js';

export function openModal({
  title,
  message,
  fields = [],
  confirmLabel = 'OK',
  cancelLabel  = 'Отмена',
  danger = false,
} = {}) {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'app-modal' + (danger ? ' danger' : '');

    const inputs = fields.map(renderField).join('');

    dlg.innerHTML = `
      <form method="dialog" class="modal-form">
        <h3 class="modal-title">${escapeHtml(title)}</h3>
        ${message ? `<p class="modal-message">${escapeHtml(message)}</p>` : ''}
        ${inputs}
        <div class="modal-actions">
          <button type="button" class="modal-cancel" value="cancel">${escapeHtml(cancelLabel)}</button>
          <button type="submit" class="modal-confirm${danger ? ' danger' : ''}" value="confirm">${escapeHtml(confirmLabel)}</button>
        </div>
      </form>
    `;

    document.body.appendChild(dlg);

    let settled = false;
    const cleanup = () => {
      if (dlg.parentNode) dlg.parentNode.removeChild(dlg);
    };
    const finish = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    dlg.querySelector('.modal-cancel').addEventListener('click', () => {
      dlg.close('cancel');
    });
    dlg.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();
      const result = collectFields(dlg, fields);
      dlg.close('confirm');
      finish(result);
    });
    dlg.addEventListener('close', () => finish(null));
    // Close on backdrop click — native <dialog> doesn't do this for us.
    // Раньше использовался `e.target === dlg`, но клик в padding самого
    // dialog тоже даёт target=dlg и закрывал модал прямо во время
    // заполнения формы (между полями). Теперь проверяем bounding-box:
    // закрываем только если клик физически за пределами dialog rect
    // (то есть на ::backdrop).
    dlg.addEventListener('click', (e) => {
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right &&
        e.clientY >= r.top  && e.clientY <= r.bottom;
      if (!inside) dlg.close('cancel');
    });

    // type:'ref' — связать с кнопкой выбора из справочника (СПР-1b).
    for (const f of fields) bindRefField(dlg, f);

    dlg.showModal();
    // Autofocus first field if any, otherwise the confirm button.
    const firstInput = dlg.querySelector('input, select, textarea');
    (firstInput ?? dlg.querySelector('.modal-confirm')).focus();
  });
}
